import React, { useState, useEffect } from 'react';
import { adminApi } from '../api/adminApi';
import toast, { Toaster } from 'react-hot-toast';
import PremiumTable from '../components/common/PremiumTable';
import PageHeader from '../components/common/PageHeader';
import { useNavigate } from 'react-router-dom';
import { PencilSquareIcon, NoSymbolIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getAllUsers();
      setUsers(res.data.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBlock = async (userId, currentStatus) => {
    try {
      await adminApi.updateUserStatus(userId, !currentStatus);
      toast.success(`User ${currentStatus ? 'blocked' : 'unblocked'} successfully`);
      fetchUsers(); // refresh
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update user status');
    }
  };

  const columns = [
    {
      header: 'User Details',
      accessor: 'name',
      render: (row) => (
        <div className="flex items-center">
          <div className="h-10 w-10 flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold">
              {row.name?.charAt(0)?.toUpperCase()}
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">{row.name}</div>
            <div className="text-sm text-gray-500">{row.email}</div>
          </div>
        </div>
      )
    },
    {
      header: 'Role / Business',
      accessor: 'role',
      render: (row) => (
        <div>
          <div className="text-sm text-gray-900 capitalize">{row.role}</div>
          <div className="text-sm text-gray-500">{row.business?.businessName || '-'}</div>
        </div>
      )
    },
    {
      header: 'Joined Date',
      accessor: 'createdAt',
      render: (row) => (
        <span className="text-sm text-gray-500">
          {new Date(row.createdAt).toLocaleDateString()}
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'isActive',
      render: (row) => (
        row.isActive 
          ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Active</span>
          : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Blocked</span>
      )
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigate(`/users/edit/${row._id}`, { state: { user: row } })}
            className="flex items-center px-3 py-1.5 bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-full text-xs font-bold transition-all shadow-sm hover:shadow"
          >
            <PencilSquareIcon className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
            Edit
          </button>
          <button
            onClick={() => handleToggleBlock(row._id, row.isActive)}
            className={`flex items-center px-3 py-1.5 border rounded-full text-xs font-bold transition-all shadow-sm hover:shadow ${
              row.isActive 
                ? 'bg-red-50 text-red-700 border-red-100 hover:bg-red-100' 
                : 'bg-green-50 text-green-700 border-green-100 hover:bg-green-100'
            }`}
          >
            {row.isActive ? (
              <>
                <NoSymbolIcon className="w-3.5 h-3.5 mr-1.5 text-red-500" />
                Block
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-3.5 h-3.5 mr-1.5 text-green-500" />
                Unblock
              </>
            )}
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-transparent pb-12">
      <Toaster position="top-right" />
      <PageHeader 
        title="User Management"
        subtitle="Manage and organize your platform data seamlessly."
        actionButton={
          <button 
            onClick={() => navigate('/users/create')}
            className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-full font-bold text-sm shadow-sm transition-all transform hover:-translate-y-0.5 hover:shadow-md"
          >
            + Create New User
          </button>
        }
      />
      <main className="max-w-7xl">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading users...</div>
        ) : (
          <PremiumTable 
            data={users}
            columns={columns}
            searchKeys={['name', 'email', 'role', 'business.businessName']}
            primaryKey="_id"
            paginationMode="standard"
            itemsPerPage={10}
          />
        )}
      </main>
    </div>
  );
};

export default Users;
